import { useEffect, useState, type ReactNode } from 'react';
import { MAX_MINUTOS_CARGA } from '@matias-asset-os/contracts';
import type {
  CategoriaGasto,
  Incidencia,
  PropiedadResumen,
  TipoHora,
} from '@matias-asset-os/contracts';

/** Lo que devuelve /api/usuarios: sólo lo necesario para elegir a una persona. */
interface PersonaEquipo {
  id: string;
  nombre: string;
  rol: string;
}
import { formatearDia, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { incidenciaActiva } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';

/**
 * Carga de horas y gastos por pasos.
 *
 * Es la pantalla que se pidió expresamente: «todo tiene que ser en sistema de
 * botones, uno selecciona lo que quiere hacer y vamos presionando A/B/C; con 5
 * o 6 toques se carga un reporte de hora o de gasto».
 *
 * De ahí las tres decisiones de diseño:
 *   - una sola pregunta por pantalla, con respuestas grandes;
 *   - cada respuesta avanza sola, sin botón de «siguiente»;
 *   - el resumen final se puede corregir volviendo atrás paso a paso.
 *
 * Reglas propias de esta versión, ya contra el backend real:
 *   - Dirección puede imputar el trabajo de otra persona; el resto sólo el suyo,
 *     y eso lo vuelve a comprobar el servidor: aquí sólo se decide qué enseñar.
 *   - En los gastos la foto del comprobante es obligatoria y se sube de verdad
 *     como adjunto del gasto recién creado. El importe se teclea: no se dice en
 *     ningún sitio que salga de leer la foto, porque no hay nada que la lea.
 *   - Un gasto interno no es repercutible; el asistente ni siquiera pregunta.
 */

type Tipo = 'GASTO' | 'HORAS';
type Destino = 'CLIENTE' | 'INTERNO';

interface Borrador {
  tipo: Tipo;
  deQuien?: string;
  destino?: Destino;
  propiedadId?: string;
  /** Las incidencias abiertas a las que responde; pueden ser varias. */
  incidencias?: string[];
  /** Lo que se hizo, escrito a mano. Se puede sumar a las incidencias. */
  descripcionManual?: string;
  /** Alguien que no usa la aplicacion: un proveedor, un operario suelto. */
  personaManual?: string;
  /** Coste y precio por hora, en céntimos. */
  costeHora?: number;
  precioHora?: number;
  /** Si las horas se cobran aparte o van dentro del fee. */
  extra?: boolean;
  trabajo?: TipoHora;
  categoria?: CategoriaGasto;
  facturable?: boolean;
  importeCents?: number;
  minutos?: number;
  fecha?: string;
  concepto?: string;
  foto?: File;
}

/*
  Los atajos de tiempo, con la jornada dentro.

  Faltaban las dos duraciones mas frecuentes: media jornada y jornada entera.
  Sin ellas, el dia completo habia que escribirlo a mano en el campo libre, que
  es justo lo que un atajo viene a evitar.
*/
const MINUTOS = [30, 60, 90, 120, 180, 240, 360, 480];
const IMPORTES = [1500, 3000, 6000, 12000, 25000, 50000];

const TRABAJOS: TipoHora[] = [
  'GESTION',
  'MANTENIMIENTO',
  'LIMPIEZA',
  'OBRA',
  'ADMINISTRACION',
  'COMERCIAL',
  'DESPLAZAMIENTO',
  'OTRO',
];

const CATEGORIAS: CategoriaGasto[] = [
  'PROVEEDORES_CLIENTES',
  'VEHICULOS',
  'LIMPIEZA',
  'OFICINA',
  'MARKETING',
  'PROPERTY_MANAGEMENT',
  'SERVICIOS',
  'OTRO',
];

/**
 * Tiempo escrito a mano → minutos. Null si no se entiende.
 *   «45» → 45 h · «1,5» o «1.5» → 1 h 30′ · «1:45» → 1 h 45′
 * Con dos puntos son horas y minutos; con coma o punto, horas con decimales.
 */
export function minutosDeTexto(bruto: string): number | null {
  const limpio = bruto.trim().replace(/\s|h$/gi, '');
  if (!limpio) return null;
  let minutos: number;
  const hm = /^(\d+):([0-5]?\d)$/.exec(limpio);
  if (hm) {
    minutos = Number(hm[1]) * 60 + Number(hm[2]);
  } else {
    const valor = Number(limpio.replace(',', '.'));
    if (!Number.isFinite(valor)) return null;
    minutos = Math.round(valor * 60);
  }
  if (minutos <= 0 || minutos > MAX_MINUTOS_CARGA) return null;
  return minutos;
}

export function textoDeMinutos(minutos: number): string {
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (!horas) return `${resto} min`;
  return resto ? `${horas} h ${resto}′` : `${horas} h`;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** «20», «20,50», «20.5» → céntimos. Null si no es un importe. */
export function centimosDeTexto(bruto: string): number | null {
  const limpio = bruto.trim().replace(/\s|€/g, '').replace(',', '.');
  if (!limpio) return null;
  const valor = Number(limpio);
  if (!Number.isFinite(valor) || valor < 0 || valor > 100_000) return null;
  return Math.round(valor * 100);
}

function textoDeCentimos(c: number | null | undefined): string {
  if (c === null || c === undefined) return '';
  return (c / 100).toFixed(2).replace('.', ',').replace(/,00$/, '');
}

export function CargaRapida({
  idioma,
  rol,
  onHecho,
  onSalir,
}: {
  idioma: Idioma;
  rol: string;
  onHecho: () => void;
  onSalir: () => void;
}) {
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [paso, setPaso] = useState(0);
  const [manual, setManual] = useState('');
  const [errorManual, setErrorManual] = useState(false);
  const [fechaManual, setFechaManual] = useState('');
  const [errorFecha, setErrorFecha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [propiedades, setPropiedades] = useState<PropiedadResumen[]>([]);
  const [equipo, setEquipo] = useState<PersonaEquipo[]>([]);
  const [abiertas, setAbiertas] = useState<Incidencia[]>([]);
  const [personaTexto, setPersonaTexto] = useState('');
  const [escribePersona, setEscribePersona] = useState(false);
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [conDescripcion, setConDescripcion] = useState(false);
  const [descripcionTexto, setDescripcionTexto] = useState('');
  const [costeTexto, setCosteTexto] = useState('');
  const [precioTexto, setPrecioTexto] = useState('');
  const [errorPrecio, setErrorPrecio] = useState<string | null>(null);

  const imputaAOtros = rol === 'DIRECTOR';
  const dinero = (c: number) =>
    new Intl.NumberFormat(idioma === 'es' ? 'es-ES' : idioma === 'de' ? 'de-DE' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(c / 100);

  useEffect(() => {
    api
      .propiedades()
      // El estado en el modelo es ACTIVE, no ACTIVA: filtrar por el nombre en
      // castellano dejaba la lista de propiedades vacia y el asistente muerto.
      .then((r) => setPropiedades(r.propiedades.filter((p) => p.estado === 'ACTIVE')))
      .catch(() => undefined);
    if (imputaAOtros) {
      api
        .usuarios()
        .then((r) => setEquipo(r.usuarios.filter((u) => u.rol === 'DIRECTOR' || u.rol === 'MANAGER')))
        .catch(() => undefined);
    }
  }, [imputaAOtros]);

  // Las incidencias abiertas se piden sólo cuando ya hay propiedad elegida.
  useEffect(() => {
    if (!borrador?.propiedadId) {
      setAbiertas([]);
      return;
    }
    api
      .incidencias(borrador.propiedadId)
      .then((r) => setAbiertas(r.incidencias.filter((i) => incidenciaActiva(i.estado))))
      .catch(() => setAbiertas([]));
  }, [borrador?.propiedadId]);

  /*
    LA TARIFA QUE SE PROPONE. En cuanto se sabe de quien son las horas y de que
    casa, se piden su coste y su precio por hora para dejarlos escritos: casi
    siempre se aceptan tal cual, y si no se corrigen ahi mismo. Solo Direccion
    ve y decide lo que cuesta y lo que se cobra una hora.
  */
  const esHoras = borrador?.tipo === 'HORAS';
  const deQuien = borrador?.deQuien ?? null;
  const tercero = borrador?.personaManual ?? null;
  const casaElegida = borrador?.propiedadId ?? null;
  useEffect(() => {
    if (!imputaAOtros || !esHoras || tercero) return;
    let vigente = true;
    api
      .finTarifaSugerida(deQuien, casaElegida)
      .then((r) => {
        if (!vigente) return;
        setCosteTexto(textoDeCentimos(r.tarifa.costeHoraCents));
        setPrecioTexto(textoDeCentimos(r.tarifa.precioHoraClienteCents));
      })
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, [imputaAOtros, esHoras, deQuien, tercero, casaElegida]);

  const avanzar = (cambio: Partial<Borrador>) => {
    setBorrador((actual) => ({ ...(actual as Borrador), ...cambio }));
    setPaso((p) => p + 1);
  };

  const retroceder = () => {
    setError(null);
    if (paso === 0) {
      if (borrador) {
        setBorrador(null);
        return;
      }
      onSalir();
      return;
    }
    setPaso((p) => p - 1);
  };

  const reiniciar = () => {
    setBorrador(null);
    setPaso(0);
    setEnviado(false);
    setManual('');
    setError(null);
    setPersonaTexto('');
    setEscribePersona(false);
    setElegidas([]);
    setConDescripcion(false);
    setDescripcionTexto('');
    setCosteTexto('');
    setPrecioTexto('');
    setErrorPrecio(null);
  };

  // --- Paso 0 · qué se quiere registrar ------------------------------------
  if (!borrador) {
    return (
      <div className="rapido">
        <div className="fila-flex entre">
          <button type="button" className="enlace" onClick={onSalir}>
            <Icono nombre="volver" /> {t(idioma, 'rapido.atras')}
          </button>
        </div>
        <p className="pregunta">{t(idioma, 'rapido.titulo')}</p>
        <div className="opciones dos">
          <button type="button" className="opcion" onClick={() => setBorrador({ tipo: 'GASTO' })}>
            <b>{t(idioma, 'rapido.gasto')}</b>
            <small>{t(idioma, 'rapido.comprobante')}</small>
          </button>
          <button type="button" className="opcion" onClick={() => setBorrador({ tipo: 'HORAS' })}>
            <b>{t(idioma, 'rapido.horas')}</b>
            <small>{t(idioma, 'rapido.horasPie')}</small>
          </button>
        </div>
      </div>
    );
  }

  const preguntas: Array<() => ReactNode> = [];

  // 1 · de quién son (sólo Dirección)
  if (imputaAOtros) {
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.deQuienSon')}</p>
        <p className="suave">{t(idioma, 'rapido.deQuienSonPie')}</p>
        <div className="opciones">
          {equipo.map((u) => (
            <button
              key={u.id}
              type="button"
              className="opcion"
              onClick={() => avanzar({ deQuien: u.id, personaManual: undefined })}
            >
              <b>{u.nombre}</b>
              <small>{t(idioma, `rol.${u.rol}`)}</small>
            </button>
          ))}
          <button
            type="button"
            className="opcion"
            onClick={() => avanzar({ deQuien: undefined, personaManual: undefined })}
          >
            <b>{t(idioma, 'rapido.mias')}</b>
          </button>
          {/* Las horas de alguien que no esta en la aplicacion: un proveedor. */}
          {borrador.tipo === 'HORAS' && (
            <button
              type="button"
              className={`opcion${escribePersona ? ' elegida' : ''}`}
              aria-expanded={escribePersona}
              onClick={() => setEscribePersona((v) => !v)}
            >
              <b>{t(idioma, 'rapido.otraPersona')}</b>
              <small>{t(idioma, 'rapido.otraPersonaPie')}</small>
            </button>
          )}
        </div>
        {borrador.tipo === 'HORAS' && escribePersona && (
          <div className="tiempo-manual">
            <div className="campo">
              <label htmlFor="persona-manual">{t(idioma, 'rapido.nombrePersona')}</label>
              <input
                id="persona-manual"
                type="text"
                autoFocus
                maxLength={200}
                value={personaTexto}
                onChange={(e) => setPersonaTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && personaTexto.trim().length >= 2) {
                    e.preventDefault();
                    setCosteTexto('');
                    setPrecioTexto('');
                    avanzar({ deQuien: undefined, personaManual: personaTexto.trim() });
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="secundario"
              disabled={personaTexto.trim().length < 2}
              onClick={() => {
                setCosteTexto('');
                setPrecioTexto('');
                avanzar({ deQuien: undefined, personaManual: personaTexto.trim() });
              }}
            >
              {t(idioma, 'rapido.confirmar')}
            </button>
          </div>
        )}
      </>
    ));
  }

  // 2 · de quién es el trabajo
  preguntas.push(() => (
    <>
      <p className="pregunta">{t(idioma, 'rapido.paraQuien')}</p>
      <div className="opciones dos">
        <button type="button" className="opcion" onClick={() => avanzar({ destino: 'CLIENTE' })}>
          <b>{t(idioma, 'rapido.cliente')}</b>
          <small>{t(idioma, 'rapido.clientePie')}</small>
        </button>
        <button
          type="button"
          className="opcion"
          onClick={() =>
            avanzar({ destino: 'INTERNO', propiedadId: undefined, incidencias: [], extra: false })
          }
        >
          <b>{t(idioma, 'rapido.interno')}</b>
          <small>{t(idioma, 'rapido.internoPie')}</small>
        </button>
      </div>
    </>
  ));

  if (borrador.destino === 'CLIENTE') {
    // 3 · qué propiedad
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.queCliente')}</p>
        <div className="opciones">
          {propiedades.map((p) => (
            <button
              key={p.id}
              type="button"
              className="opcion"
              onClick={() => avanzar({ propiedadId: p.id })}
            >
              <b>{p.nombre}</b>
              <small>
                {p.codigo}
                {p.zona ? ` · ${p.zona}` : ''}
              </small>
            </button>
          ))}
          {propiedades.length === 0 && <p className="suave">{t(idioma, 'propiedades.vacio')}</p>}
        </div>
      </>
    ));

    /*
      4 · a que responde el trabajo. Puede ser a VARIAS incidencias abiertas a la
      vez —se revisa la fuga y de paso el grifo—, a ninguna, y se le puede sumar
      una descripcion escrita a mano. Por eso aqui no se avanza al tocar: se
      marca lo que toque y se continua.
    */
    const alternar = (id: string) =>
      setElegidas((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));
    const continuarIncidencias = () =>
      avanzar({
        incidencias: elegidas.filter((id) => abiertas.some((i) => i.id === id)),
        descripcionManual: conDescripcion ? descripcionTexto.trim() || undefined : undefined,
      });
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.vinculado')}</p>
        <p className="suave">{t(idioma, 'rapido.vinculadoPie')}</p>
        <div className="opciones">
          {abiertas.slice(0, 30).map((i) => {
            const marcada = elegidas.includes(i.id);
            return (
              <button
                key={i.id}
                type="button"
                className={`opcion marcable${marcada ? ' elegida' : ''}`}
                aria-pressed={marcada}
                onClick={() => alternar(i.id)}
              >
                <span className="marca" aria-hidden="true">
                  {marcada ? '✓' : ''}
                </span>
                <b>{i.titulo}</b>
                <small>
                  {i.codigo} · N{i.nivel}
                </small>
              </button>
            );
          })}
          <button
            type="button"
            className={`opcion marcable${conDescripcion ? ' elegida' : ''}`}
            aria-pressed={conDescripcion}
            onClick={() => setConDescripcion((v) => !v)}
          >
            <span className="marca" aria-hidden="true">
              {conDescripcion ? '✓' : ''}
            </span>
            <b>{t(idioma, 'rapido.descripcionManual')}</b>
            <small>{t(idioma, 'rapido.descripcionManualPie')}</small>
          </button>
        </div>
        {conDescripcion && (
          <div className="campo" style={{ marginTop: '0.75rem' }}>
            <label htmlFor="descripcion-manual">{t(idioma, 'rapido.queSeHizo')}</label>
            <textarea
              id="descripcion-manual"
              rows={3}
              maxLength={2000}
              autoFocus
              value={descripcionTexto}
              onChange={(e) => setDescripcionTexto(e.target.value)}
            />
          </div>
        )}
        <div className="fila-flex" style={{ gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" onClick={continuarIncidencias}>
            {elegidas.length === 0 && !(conDescripcion && descripcionTexto.trim())
              ? t(idioma, 'rapido.aislado')
              : t(idioma, 'rapido.continuar')}
          </button>
        </div>
      </>
    ));
  }

  if (borrador.tipo === 'HORAS') {
    // 5 · naturaleza del trabajo
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.queTrabajo')}</p>
        <p className="suave">{t(idioma, 'rapido.queTrabajoPie')}</p>
        <div className="opciones dos">
          {TRABAJOS.map((tr) => (
            <button
              key={tr}
              type="button"
              className="opcion"
              onClick={() => avanzar({ trabajo: tr })}
            >
              <b>{t(idioma, `tipoHora.${tr}`)}</b>
            </button>
          ))}
        </div>
      </>
    ));

    // 6 · cuánto tiempo
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.cuantasHoras')}</p>
        <div className="opciones dos">
          {MINUTOS.map((m) => (
            <button key={m} type="button" className="opcion" onClick={() => avanzar({ minutos: m })}>
              <b>{textoDeMinutos(m)}</b>
            </button>
          ))}
        </div>
        <div className="tiempo-manual">
          <div className="campo">
            <label htmlFor="tiempo-manual">{t(idioma, 'rapido.tiempoManual')}</label>
            <input
              id="tiempo-manual"
              type="text"
              inputMode="decimal"
              placeholder={t(idioma, 'rapido.tiempoEjemplo')}
              value={manual}
              onChange={(e) => {
                setManual(e.target.value);
                setErrorManual(false);
              }}
              aria-describedby="tiempo-manual-ayuda"
              aria-invalid={errorManual}
            />
            <p id="tiempo-manual-ayuda" className="suave" style={{ fontSize: '0.75rem' }}>
              {errorManual
                ? t(idioma, 'rapido.tiempoInvalido')
                : t(idioma, 'rapido.tiempoManualPie')}
            </p>
          </div>
          <button
            type="button"
            className="secundario"
            onClick={() => {
              const m = minutosDeTexto(manual);
              if (m === null) {
                setErrorManual(true);
                return;
              }
              avanzar({ minutos: m });
            }}
          >
            {t(idioma, 'rapido.confirmar')}
          </button>
        </div>
      </>
    ));

    /*
      7 · LO QUE CUESTA Y LO QUE SE COBRA. Direccion ve la tarifa propuesta y la
      corrige para este trabajo si hace falta. Si las horas son de una casa, se
      decide ademas si se cobran aparte (salen en sus Gastos al precio para el
      cliente) o van dentro del fee (el cliente las ve a 0 €). Un manager solo
      responde esto ultimo: lo que cuesta su hora no lo decide el.
    */
    const deCliente = borrador.destino === 'CLIENTE';
    if (imputaAOtros || deCliente) {
      const cerrar = (extra: boolean) => {
        if (!imputaAOtros) {
          avanzar({ extra });
          return;
        }
        const coste = centimosDeTexto(costeTexto);
        if (coste === null) {
          setErrorPrecio(t(idioma, 'rapido.costeHoraInvalido'));
          return;
        }
        const precio = precioTexto.trim() ? centimosDeTexto(precioTexto) : 0;
        if (precio === null || (extra && !precioTexto.trim())) {
          setErrorPrecio(t(idioma, 'rapido.precioHoraInvalido'));
          return;
        }
        setErrorPrecio(null);
        avanzar({ extra: deCliente && extra, costeHora: coste, precioHora: precio });
      };
      const horas = (borrador.minutos ?? 0) / 60;
      const costeVisto = centimosDeTexto(costeTexto);
      const precioVisto = centimosDeTexto(precioTexto);
      preguntas.push(() => (
        <>
          <p className="pregunta">
            {t(idioma, imputaAOtros ? 'rapido.costeYPrecio' : 'rapido.facturableExtra')}
          </p>
          {imputaAOtros && (
            <>
              <p className="suave">{t(idioma, 'rapido.costeYPrecioPie')}</p>
              <div className="precios-hora">
                <div className="campo">
                  <label htmlFor="coste-hora">{t(idioma, 'rapido.costeHora')}</label>
                  <input
                    id="coste-hora"
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={costeTexto}
                    onChange={(e) => {
                      setCosteTexto(e.target.value);
                      setErrorPrecio(null);
                    }}
                  />
                  {costeVisto !== null && horas > 0 && (
                    <small className="suave numerico">
                      {textoDeMinutos(borrador.minutos ?? 0)} → {dinero(Math.round(costeVisto * horas))}
                    </small>
                  )}
                </div>
                {deCliente && (
                  <div className="campo">
                    <label htmlFor="precio-hora">{t(idioma, 'rapido.precioHora')}</label>
                    <input
                      id="precio-hora"
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={precioTexto}
                      onChange={(e) => {
                        setPrecioTexto(e.target.value);
                        setErrorPrecio(null);
                      }}
                    />
                    {precioVisto !== null && horas > 0 && (
                      <small className="suave numerico">
                        {textoDeMinutos(borrador.minutos ?? 0)} → {dinero(Math.round(precioVisto * horas))}
                      </small>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {errorPrecio && (
            <p className="aviso-demo" role="alert">
              {errorPrecio}
            </p>
          )}
          {deCliente ? (
            <>
              {imputaAOtros && <p className="pregunta-menor">{t(idioma, 'rapido.facturableExtra')}</p>}
              <div className="opciones dos">
                <button type="button" className="opcion" onClick={() => cerrar(true)}>
                  <b>{t(idioma, 'rapido.si')}</b>
                  <small>{t(idioma, 'rapido.extraSi')}</small>
                </button>
                <button type="button" className="opcion" onClick={() => cerrar(false)}>
                  <b>{t(idioma, 'rapido.no')}</b>
                  <small>{t(idioma, 'rapido.extraNo')}</small>
                </button>
              </div>
            </>
          ) : (
            <button type="button" onClick={() => cerrar(false)}>
              {t(idioma, 'rapido.continuar')}
            </button>
          )}
        </>
      ));
    }
  } else {
    // 5 · categoría del gasto
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.queCategoria')}</p>
        <div className="opciones dos">
          {CATEGORIAS.map((c) => (
            <button
              key={c}
              type="button"
              className="opcion"
              onClick={() => avanzar({ categoria: c })}
            >
              <b>{t(idioma, `categoriaGasto.${c}`)}</b>
            </button>
          ))}
        </div>
      </>
    ));

    // 6 · el comprobante, obligatorio
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.comprobante')}</p>
        <p className="suave">{t(idioma, 'rapido.fotoObligatoria')}</p>
        <div className="opciones">
          <label className="opcion" style={{ cursor: 'pointer' }}>
            <b>
              <Icono nombre="camara" /> {t(idioma, 'rapido.hacerFoto')}
            </b>
            <small>{t(idioma, 'rapido.formatosFoto')}</small>
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) avanzar({ foto: f });
              }}
            />
          </label>
        </div>
      </>
    ));

    // 7 · importe
    preguntas.push(() => (
      <>
        <p className="pregunta">{t(idioma, 'rapido.cuanto')}</p>
        {borrador.foto && <p className="suave">📎 {borrador.foto.name}</p>}
        <div className="opciones dos">
          {IMPORTES.map((c) => (
            <button
              key={c}
              type="button"
              className="opcion"
              onClick={() => avanzar({ importeCents: c })}
            >
              <b>{dinero(c)}</b>
            </button>
          ))}
        </div>
        <div className="tiempo-manual">
          <div className="campo">
            <label htmlFor="importe-manual">{t(idioma, 'rapido.importeManual')}</label>
            <input
              id="importe-manual"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={manual}
              onChange={(e) => {
                setManual(e.target.value);
                setErrorManual(false);
              }}
              aria-invalid={errorManual}
            />
          </div>
          <button
            type="button"
            className="secundario"
            onClick={() => {
              const n = Number(manual.replace(',', '.'));
              if (!Number.isFinite(n) || n <= 0) {
                setErrorManual(true);
                return;
              }
              setManual('');
              avanzar({ importeCents: Math.round(n * 100) });
            }}
          >
            {t(idioma, 'rapido.confirmar')}
          </button>
        </div>
      </>
    ));

    // 8 · repercutible al cliente (sólo si hay propiedad)
    if (borrador.destino === 'CLIENTE') {
      preguntas.push(() => (
        <>
          <p className="pregunta">{t(idioma, 'rapido.repercutible')}</p>
          <p className="suave">{t(idioma, 'rapido.repercutiblePie')}</p>
          <div className="opciones dos">
            <button type="button" className="opcion" onClick={() => avanzar({ facturable: true })}>
              <b>{t(idioma, 'rapido.si')}</b>
              <small>{t(idioma, 'rapido.entraEnFactura')}</small>
            </button>
            <button type="button" className="opcion" onClick={() => avanzar({ facturable: false })}>
              <b>{t(idioma, 'rapido.no')}</b>
              <small>{t(idioma, 'rapido.loAsumimos')}</small>
            </button>
          </div>
        </>
      ));
    }
  }

  // Último · qué día
  const hoy = iso(new Date());
  const ayer = iso(new Date(Date.now() - 86_400_000));

  /*
    Solo se acepta una fecha entera y con sentido: de este siglo y no del
    futuro. Un gasto de «0002» o de dentro de un mes no es un dato, es una
    errata que acaba contando en el mes equivocado.
  */
  function confirmarFecha() {
    const valida = /^\d{4}-\d{2}-\d{2}$/.test(fechaManual);
    if (!valida || fechaManual < '2000-01-01' || fechaManual > hoy) {
      setErrorFecha(true);
      return;
    }
    avanzar({ fecha: fechaManual });
  }
  preguntas.push(() => (
    <>
      <p className="pregunta">{t(idioma, 'rapido.cuando')}</p>
      <div className="opciones dos">
        <button type="button" className="opcion" onClick={() => avanzar({ fecha: hoy })}>
          <b>{t(idioma, 'rapido.hoy')}</b>
          <small>{formatearDia(hoy)}</small>
        </button>
        <button type="button" className="opcion" onClick={() => avanzar({ fecha: ayer })}>
          <b>{t(idioma, 'rapido.ayer')}</b>
          <small>{formatearDia(ayer)}</small>
        </button>
      </div>
      {/*
        LA FECHA A MANO NO AVANZA SOLA. Antes saltaba a la pantalla siguiente en
        cuanto el campo tenia una fecha valida, y el navegador da por valida la
        fecha a mitad de escribir el año: al teclear el primer «2» de 2025 ya
        habia un 25-07-0002, y la carga se iba con el año dos. Ahora se escribe
        entera y se confirma, igual que el tiempo a mano de la pregunta de horas.
      */}
      <div className="tiempo-manual">
        <div className="campo">
          <label htmlFor="fecha-manual">{t(idioma, 'rapido.otraFecha')}</label>
          <input
            id="fecha-manual"
            type="date"
            min="2000-01-01"
            max={hoy}
            value={fechaManual}
            onChange={(e) => {
              setFechaManual(e.target.value);
              setErrorFecha(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmarFecha();
              }
            }}
            aria-describedby="fecha-manual-ayuda"
            aria-invalid={errorFecha}
          />
          {errorFecha && (
            <p id="fecha-manual-ayuda" className="suave" style={{ fontSize: '0.75rem' }}>
              {t(idioma, 'rapido.fechaInvalida')}
            </p>
          )}
        </div>
        <button type="button" className="secundario" onClick={confirmarFecha}>
          {t(idioma, 'rapido.confirmar')}
        </button>
      </div>
    </>
  ));

  // --- Envío ----------------------------------------------------------------

  const propiedad = propiedades.find((p) => p.id === borrador.propiedadId);
  const vinculadas = abiertas.filter((i) => borrador.incidencias?.includes(i.id));
  const incidencia = vinculadas[0];

  const conceptoPorDefecto =
    borrador.tipo === 'GASTO'
      ? (incidencia?.titulo ?? t(idioma, `categoriaGasto.${borrador.categoria ?? 'OTRO'}`))
      : (incidencia?.titulo ?? t(idioma, `tipoHora.${borrador.trabajo ?? 'OTRO'}`));
  // Lo que se lee en la columna «Descripción»: lo escrito a mano y la nota.
  const descripcionFinal =
    [borrador.descripcionManual, borrador.concepto?.trim()].filter(Boolean).join(' · ') || null;

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      if (borrador.tipo === 'GASTO') {
        const r = await api.finRegistrarGasto({
          propertyId: borrador.propiedadId ?? null,
          fecha: borrador.fecha ?? hoy,
          concepto: (borrador.concepto?.trim() || conceptoPorDefecto).slice(0, 300),
          categoria: borrador.categoria ?? 'OTRO',
          costeCents: borrador.importeCents ?? 0,
          facturableCliente: borrador.destino === 'CLIENTE' && borrador.facturable === true,
          descripcion: borrador.descripcionManual ?? null,
          incidencias: borrador.incidencias ?? [],
        });
        // El comprobante se adjunta al gasto recién creado. Si esto fallara, el
        // gasto ya existe: se avisa en vez de fingir que todo fue bien.
        if (borrador.foto && r.gasto?.id) {
          await api.subirAdjunto('GASTO', r.gasto.id, borrador.foto);
        }
      } else {
        await api.finRegistrarHoras({
          ...(borrador.personaManual
            ? { personaManual: borrador.personaManual }
            : { userId: borrador.deQuien }),
          propertyId: borrador.propiedadId ?? null,
          fecha: borrador.fecha ?? hoy,
          minutos: borrador.minutos ?? 0,
          tipo: borrador.trabajo ?? 'OTRO',
          /*
            LA NOTA EN BLANCO GUARDA LO QUE PONE EN GRIS.

            El campo venia con el titulo de la incidencia escrito en gris, como
            sugerencia, y quien lo dejaba en blanco —que es lo normal: ya lo dice
            ahi— guardaba la hora SIN descripcion. En la tabla de Horas salia un
            guion. Era justo al reves de lo que la pantalla prometia: ensenaba el
            texto y luego no lo guardaba.

            Ahora en blanco significa «lo que pone ahi», con incidencia o sin
            ella. Escribir algo distinto lo sustituye, que para eso se escribe.
          */
          descripcion: (descripcionFinal ?? conceptoPorDefecto).slice(0, 2000),
          incidencias: borrador.incidencias ?? [],
          facturableExtra: borrador.destino === 'CLIENTE' && borrador.extra === true,
          ...(imputaAOtros
            ? {
                costeHoraCents: borrador.costeHora ?? 0,
                precioHoraClienteCents: borrador.precioHora ?? 0,
              }
            : {}),
        });
      }
      setEnviado(true);
      onHecho();
    } catch (fallo) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    } finally {
      setEnviando(false);
    }
  };

  const fila = (etiqueta: string, valor: ReactNode) => (
    <div className="fila entre">
      <span className="suave">{etiqueta}</span>
      <span>{valor}</span>
    </div>
  );

  const resumen = (
    <>
      <p className="pregunta">{t(idioma, 'rapido.revisar')}</p>
      <section className="tarjeta">
        <div className="lista">
          {fila(
            t(idioma, 'rapido.queEs'),
            t(idioma, borrador.tipo === 'GASTO' ? 'rapido.gasto' : 'rapido.horas'),
          )}
          {(borrador.deQuien || borrador.personaManual) &&
            fila(
              t(idioma, 'rapido.deQuienSon'),
              borrador.personaManual ?? equipo.find((u) => u.id === borrador.deQuien)?.nombre ?? '—',
            )}
          {fila(
            t(idioma, 'rapido.paraQuien'),
            borrador.destino === 'INTERNO'
              ? t(idioma, 'rapido.interno')
              : (propiedad?.nombre ?? '—'),
          )}
          {borrador.destino === 'CLIENTE' &&
            fila(
              t(idioma, 'ficha.incidencias'),
              vinculadas.length > 0
                ? vinculadas.map((i) => `${i.codigo} – ${i.titulo}`).join(' · ')
                : t(idioma, 'rapido.aislado'),
            )}
          {borrador.descripcionManual &&
            fila(t(idioma, 'gastos.descripcion'), borrador.descripcionManual)}
          {borrador.tipo === 'HORAS' && (
            <>
              {fila(
                t(idioma, 'rapido.queTrabajo'),
                borrador.trabajo ? t(idioma, `tipoHora.${borrador.trabajo}`) : '—',
              )}
              {fila(
                t(idioma, 'rapido.cuantasHoras'),
                <span className="numerico">{textoDeMinutos(borrador.minutos ?? 0)}</span>,
              )}
              {imputaAOtros &&
                borrador.costeHora !== undefined &&
                fila(
                  t(idioma, 'rapido.costeHora'),
                  <span className="numerico">
                    {dinero(borrador.costeHora)} ·{' '}
                    {dinero(Math.round((borrador.costeHora * (borrador.minutos ?? 0)) / 60))}
                  </span>,
                )}
              {borrador.destino === 'CLIENTE' &&
                fila(
                  t(idioma, 'rapido.facturableExtra'),
                  <span className={`pastilla ${borrador.extra ? 'verde' : 'neutra'}`}>
                    {borrador.extra
                      ? imputaAOtros
                        ? `${t(idioma, 'rapido.si')} · ${dinero(
                            Math.round(((borrador.precioHora ?? 0) * (borrador.minutos ?? 0)) / 60),
                          )}`
                        : t(idioma, 'rapido.si')
                      : `${t(idioma, 'rapido.no')} · ${dinero(0)}`}
                  </span>,
                )}
            </>
          )}
          {borrador.tipo === 'GASTO' && (
            <>
              {fila(
                t(idioma, 'interno.categoria'),
                borrador.categoria ? t(idioma, `categoriaGasto.${borrador.categoria}`) : '—',
              )}
              {fila(
                t(idioma, 'rapido.importe'),
                <span className="numerico">{dinero(borrador.importeCents ?? 0)}</span>,
              )}
              {fila(t(idioma, 'rapido.comprobante'), borrador.foto ? `📎 ${borrador.foto.name}` : '—')}
              {borrador.destino === 'CLIENTE' &&
                fila(
                  t(idioma, 'rapido.repercutible'),
                  <span className={`pastilla ${borrador.facturable ? 'verde' : 'neutra'}`}>
                    {t(idioma, borrador.facturable ? 'rapido.si' : 'rapido.no')}
                  </span>,
                )}
            </>
          )}
          {fila(t(idioma, 'rapido.fecha'), formatearDia(borrador.fecha ?? hoy))}
        </div>

        <div className="campo" style={{ marginTop: '0.75rem' }}>
          <label htmlFor="rapido-concepto">{t(idioma, 'rapido.notaOpcional')}</label>
          <input
            id="rapido-concepto"
            type="text"
            value={borrador.concepto ?? ''}
            placeholder={conceptoPorDefecto}
            onChange={(e) => setBorrador({ ...borrador, concepto: e.target.value })}
          />
        </div>

        {error && (
          <p className="aviso-demo" role="alert">
            {error}
          </p>
        )}

        {!enviado ? (
          <button
            type="button"
            className="acceso-entrar"
            disabled={enviando}
            onClick={() => void enviar()}
          >
            {t(idioma, 'rapido.enviar')}
          </button>
        ) : (
          <>
            <p className="pastilla verde" role="status">
              {t(idioma, 'rapido.enviado')}
            </p>
            <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" onClick={reiniciar}>
                ＋ {t(idioma, 'rapido.otroMas')}
              </button>
              <button type="button" className="secundario" onClick={onSalir}>
                {t(idioma, 'rapido.verCargado')}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );

  const pasosTotales = preguntas.length + 1;
  const actual = paso < preguntas.length ? preguntas[paso]! : () => resumen;
  const toques = paso + 1;

  return (
    <div className="rapido">
      <div className="fila-flex entre">
        <button type="button" className="enlace" onClick={retroceder}>
          <Icono nombre="volver" /> {t(idioma, 'rapido.atras')}
        </button>
        <span className="pasos">
          {Array.from({ length: pasosTotales }, (_, i) => (
            <i key={i} className={i < toques ? 'hecho' : ''} />
          ))}
          <span>{t(idioma, 'rapido.toques', { n: toques, total: pasosTotales })}</span>
        </span>
      </div>
      {actual()}
    </div>
  );
}
